import {
  Duration,
  type DurationLikeObject,
  type DurationObjectUnits,
  type DurationUnit,
  type DurationUnits,
} from 'luxon';
import { string } from 'yup';
import { assert } from '../assert';
import { i18n, uiLanguage } from '../i18n';

type Unit = keyof DurationObjectUnits;

export interface DurationValidatorOptions {
  max?: DurationLikeObject;
  min?: DurationLikeObject;
  only?: DurationUnits;
}

export const duration = (options: DurationValidatorOptions = {}) => {
  const max = options.max ? Duration.fromObject(options.max) : undefined;
  assert(max === undefined || max.isValid, `max must be valid if defined, got: ${options.max}`);

  const min = options.min ? Duration.fromObject(options.min) : undefined;
  assert(min === undefined || min.isValid, `min must be valid if defined, got: ${options.min}`);

  const only = options.only ? [options.only].flat().map(normalizeUnit) : undefined;
  assert(only === undefined || only.length > 0, `only must not be empty if defined`);

  return string().test({
    name: 'is-duration',
    skipAbsent: true,
    test: (value, ctx) => {
      if (value === '' || value === undefined) return true;

      const parsed = Duration.fromISO(value).rescale();

      if (!parsed.isValid) return false;

      if (only) {
        const durationObject = parsed.toObject();
        for (const unit of Object.keys(durationObject) as readonly Unit[]) {
          if (durationObject[unit] !== 0 && !only.includes(unit))
            return ctx.createError({
              message: i18n.t('errors.validation.duration_only', { units: unitList(only) }),
            });
        }
      }

      const millis = parsed.toMillis();

      if (min && millis < min.toMillis())
        return ctx.createError({
          message: i18n.t('errors.validation.min', { min: humanize(min) }),
        });

      if (max && millis > max.toMillis())
        return ctx.createError({
          message: i18n.t('errors.validation.max', { max: humanize(max) }),
        });

      return true;
    },
  });
};

const unitMap: Record<DurationUnit, Unit> = {
  day: 'days',
  days: 'days',
  hour: 'hours',
  hours: 'hours',
  millisecond: 'milliseconds',
  milliseconds: 'milliseconds',
  minute: 'minutes',
  minutes: 'minutes',
  month: 'months',
  months: 'months',
  quarter: 'quarters',
  quarters: 'quarters',
  second: 'seconds',
  seconds: 'seconds',
  week: 'weeks',
  weeks: 'weeks',
  year: 'years',
  years: 'years',
};

/**
 * @param unit A loosened duration unit, i.e. plural or singular
 */
function normalizeUnit(unit: DurationUnit): Unit {
  return unitMap[unit];
}

// Luxon carries its locale per value rather than globally, so the message reads it from i18n.
function humanize(value: Duration): string {
  return value.reconfigure({ locale: uiLanguage() }).toHuman();
}

// Intl knows the calendar and clock units but not Luxon's quarters and milliseconds, which
// keep their English key.
const INTL_UNITS: Partial<Record<Unit, string>> = {
  days: 'day',
  hours: 'hour',
  minutes: 'minute',
  months: 'month',
  seconds: 'second',
  weeks: 'week',
  years: 'year',
};

/** The unit names joined for the UI language, e.g. "hours, minutes, and seconds". */
function unitList(units: readonly Unit[]): string {
  const locale = uiLanguage();
  const names = units.map((unit) => {
    const intlUnit = INTL_UNITS[unit];
    if (!intlUnit) return unit;
    return (
      new Intl.NumberFormat(locale, { style: 'unit', unit: intlUnit, unitDisplay: 'long' })
        .formatToParts(2)
        .find((part) => part.type === 'unit')?.value ?? unit
    );
  });
  return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(names);
}
